import {
	SORT_COLUMNS,
	SORT_DIRECTIONS,
	SORTABLE_COLUMNS
} from '@/AdminComponent/Reactions/constants/sortValues';
import { getNextSortValue, getSortDirection } from '@/AdminComponent/Reactions/utils/tableHeaderSort';
import { reactionTableLimitAtom } from '@/data/atoms/reactionAtom';
import { ArrowDownward, ArrowUpward, SwapVert } from '@mui/icons-material';
import { useAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { Dropdown, DropdownButton, Pagination } from 'react-bootstrap';

const ReactionTable = ({ data, pagination, handlePageChange, handleLimitChange, sortBy, handleSort }) => {
	const { totalPages, currentPage, currLimit, totalVideos } = pagination;
	const [limit, setLimit] = useAtom(reactionTableLimitAtom);
	const [page, setPage] = useState(currentPage);
	// ? StartItem by default is always 1 but the parent prevents the rendition of this table in case there's no data
	const startItem = (page - 1) * limit + 1;
	const endItem = Math.min(page * limit, totalVideos);
	const hasNextPage = page < totalPages;
	const hasPrevPage = page > 1;

	useEffect(() => {
		setPage(currentPage);
		setLimit(currLimit);
	}, [currentPage, currLimit, setLimit, setPage]);

	const onLimitChange = newLimit => {
		if (newLimit === limit) return;
		setLimit(newLimit);
		handleLimitChange(newLimit);
		setPage(1);
	};

	const onPageChange = newPage => {
		setPage(newPage);
		handlePageChange(newPage);
	};
	
	const handleHeaderClick = columnKey => {
		if (!SORTABLE_COLUMNS.includes(columnKey)) return;
		const nextSortValue = getNextSortValue(columnKey, sortBy);
		handleSort(nextSortValue);
	};

	const renderSortIcon = columnKey => {
		const direction = getSortDirection(sortBy, columnKey);

		if (direction === SORT_DIRECTIONS.ASC) return <ArrowUpward fontSize="small" color='info' />;
		if (direction === SORT_DIRECTIONS.DESC) return <ArrowDownward fontSize="small" color='error' />;
		return <SwapVert fontSize="small" />;
	};

	const renderHeader = (label, columnKey) => {
		const isSortable = SORTABLE_COLUMNS.includes(columnKey);

		const headerContent = (
			<div className="d-flex align-items-center justify-content-between">
				<span className="me-1">{label}</span>
				{isSortable && renderSortIcon(columnKey)}
			</div>
		);

		return (
			<th
				key={columnKey}
				onClick={() => isSortable && handleHeaderClick(columnKey)}
				className={isSortable ? 'cursor-pointer' : ''}
			>
				{headerContent}
			</th>
		);
	};

	const renderPaginationItems = () => {
		let items = [];
		const maxPagesToShow = 5;
		const startWindow = Math.max(1, page - Math.floor(maxPagesToShow / 2));
		const endWindow = Math.min(totalPages, startWindow + maxPagesToShow - 1);

		if (startWindow > 1) {
			items.push(
				<Pagination.Item key={1} onClick={() => handlePageChange(1)}>
					1
				</Pagination.Item>
			);
			if (startWindow > 2) {
				items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
			}
		}

		for (let displayedPage = startWindow; displayedPage <= endWindow; displayedPage++) {
			items.push(
				<Pagination.Item
					key={displayedPage}
					active={displayedPage === page}
					className="cursor-pointer"
					onClick={() => handlePageChange(displayedPage)}
				>
					{displayedPage}
				</Pagination.Item>
			);
		}

		if (endWindow < totalPages) {
			if (endWindow < totalPages - 1) {
				items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
			}
			items.push(
				<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
					{totalPages}
				</Pagination.Item>
			);
		}

		return items;
	};

	return (
		<>
			<div className="d-flex justify-content-between align-items-center mb-1">
				<small className="text-muted">
					Showing {totalVideos === 0 ? 0 : startItem} to {endItem} of {totalVideos} items
				</small>
				<DropdownButton variant="info" size="sm" drop="up" id="dropdown-basic-button" title={`${limit} Items per page`}>
					{[10, 20, 50, 100].map(displayedLimit => (
						<Dropdown.Item
							key={displayedLimit}
							onClick={() => onLimitChange(displayedLimit)}
							active={displayedLimit === limit}
						>
							{displayedLimit}
						</Dropdown.Item>
					))}
				</DropdownButton>
			</div>
			<table className="table table-striped table-hover">
				<thead>
					<tr>
						{renderHeader(SORT_COLUMNS.TITLE.name, SORT_COLUMNS.TITLE.key)}
						<th>Category</th> {/* Not sortable */}
						{renderHeader(SORT_COLUMNS.INSTRUCTOR.name, SORT_COLUMNS.INSTRUCTOR.key)}
						{renderHeader(SORT_COLUMNS.LIKES.name, SORT_COLUMNS.LIKES.key)}
						{renderHeader(SORT_COLUMNS.DISLIKES.name, SORT_COLUMNS.DISLIKES.key)}
						{renderHeader(SORT_COLUMNS.VIDEO_ADDED.name, SORT_COLUMNS.VIDEO_ADDED.key)}
						{renderHeader(SORT_COLUMNS.DATE_ADDED.name, SORT_COLUMNS.DATE_ADDED.key)}
					</tr>
				</thead>
				<tbody>
					{data.length > 0 ? (
						data.map(video => (
							<tr key={video._id}>
								<td>{video.title}</td>
								<td>{video.categoryName === '' ? '—' : video.categoryName}</td>
								<td>{video.instructorName === '' ? '—' : video.instructorName}</td>
								<td>{video.likesCount}</td>
								<td>{video.dislikesCount}</td>
								<td>{video.videoAddedDate || '—'}</td>
								<td>{video.dateAdded || '—'}</td>
							</tr>
						))
					) : (
						<tr>
							<td colSpan="7" className="text-center text-muted">
								No results found for the current filters.
							</td>
						</tr>
					)}
				</tbody>
			</table>
			{totalVideos > 0 && totalPages > 1 && (
				<div className="w-100 d-flex justify-content-center mt-4">
					<Pagination>
						<Pagination.First onClick={() => onPageChange(1)} disabled={!hasPrevPage} />
						<Pagination.Prev onClick={() => onPageChange(page - 1)} disabled={!hasPrevPage} />

						{renderPaginationItems()}

						<Pagination.Next onClick={() => onPageChange(page + 1)} disabled={!hasNextPage} />
						<Pagination.Last onClick={() => onPageChange(totalPages)} disabled={!hasNextPage} />
					</Pagination>
				</div>
			)}
		</>
	);
};

export default ReactionTable;
